import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, Send, MessageSquare, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', date: '', ville: '', prestation: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Merci de remplir les champs obligatoires');
      return;
    }
    if (!consent) {
      toast.error('Veuillez accepter le traitement de vos données');
      return;
    }
    setSending(true);
    try {
      await api.post('/contact', { ...form, subject: form.prestation });
      setSent(true);
      toast.success('Message envoyé avec succès !');
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (v) => setForm(p => ({ ...p, [k]: typeof v === 'object' && v.target ? v.target.value : v }));

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 fade-in" data-testid="contact-page">
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#A1A1AA] mb-2">Contact</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>
          Parlons de votre <span className="text-[#D4AF37]">projet</span>
        </h1>
        <p className="text-base text-[#A1A1AA]">
          {"Décrivez votre événement et recevez un devis personnalisé sous 24-48h"}
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-8">
        {/* Info */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="bg-[#D4AF37]/10 p-2.5 rounded-xl shrink-0">
                <Phone className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>{"Téléphone"}</h3>
                <a href="sms:+33610507293" className="text-[#D4AF37] hover:text-[#F9E5C0] text-sm font-medium">06 10 50 72 93</a>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span className="text-xs text-[#D4AF37] font-medium">Premier contact uniquement par SMS</span>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="bg-[#D4AF37]/10 p-2.5 rounded-xl shrink-0">
                <Mail className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>Email</h3>
                <a href="mailto:rsxconceptpro@gmail.com" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37]">rsxconceptpro@gmail.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="bg-[#D4AF37]/10 p-2.5 rounded-xl shrink-0">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>{"Zone d'intervention"}</h3>
                <p className="text-sm text-[#A1A1AA]">Saint-Sauvant et alentours</p>
                <p className="text-xs text-[#A1A1AA]">Vienne (86)</p>
              </div>
            </div>
          </div>
          <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-2xl p-5">
            <h3 className="font-semibold text-sm mb-2 text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>{"Réponse rapide"}</h3>
            <p className="text-xs text-[#A1A1AA] leading-relaxed">
              {"Nous nous engageons à vous répondre sous 24 à 48 heures. Pour une demande urgente, envoyez-nous un SMS au 06 10 50 72 93."}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-3">
          {sent ? (
            <div className="bg-[#121212] border border-[#10B981]/20 rounded-2xl p-10 text-center" data-testid="contact-success">
              <CheckCircle className="w-14 h-14 text-[#10B981] mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{"Message envoyé !"}</h3>
              <p className="text-sm text-[#A1A1AA] mb-6">
                {"Nous avons bien reçu votre message et vous recontacterons sous 24-48h."}
              </p>
              <Button onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', date: '', ville: '', prestation: '', message: '' }); setConsent(false); }}
                variant="outline" className="border-white/10 rounded-full px-6" data-testid="contact-another">
                Envoyer un autre message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/5 rounded-2xl p-6 space-y-4" data-testid="contact-form">
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Nom complet *</Label>
                <Input value={form.name} onChange={set('name')} required data-testid="contact-name" placeholder="Votre nom"
                  className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Email *</Label>
                <Input type="email" value={form.email} onChange={set('email')} required data-testid="contact-email" placeholder="vous@email.com"
                  className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1.5 block">{"Téléphone"}</Label>
                <Input value={form.phone} onChange={set('phone')} data-testid="contact-phone" placeholder="06 ..."
                  className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#A1A1AA] mb-1.5 block">{"Date de l'événement"}</Label>
                  <Input type="date" value={form.date} onChange={set('date')} data-testid="contact-date"
                    className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs text-[#A1A1AA] mb-1.5 block">{"Ville de l'événement"}</Label>
                  <Input value={form.ville} onChange={set('ville')} data-testid="contact-ville" placeholder="Ville"
                    className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Type de prestation</Label>
                <Select value={form.prestation} onValueChange={set('prestation')}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 rounded-xl" data-testid="contact-prestation">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="dj">DJ & Animation</SelectItem>
                    <SelectItem value="bar">{"Bar à Mocktails"}</SelectItem>
                    <SelectItem value="serveur">{"Serveur à Domicile"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Votre message *</Label>
                <Textarea value={form.message} onChange={set('message')} required rows={5} data-testid="contact-message"
                  placeholder={"Décrivez votre projet..."} className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl resize-none" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} data-testid="contact-consent"
                  className="mt-1 accent-[#D4AF37]" />
                <span className="text-[11px] text-[#A1A1AA] leading-relaxed">
                  {"J'accepte que mes données soient traitées pour répondre à ma demande (conformément à notre politique de confidentialité). *"}
                </span>
              </label>
              <Button type="submit" disabled={sending} data-testid="contact-submit"
                className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full h-11 font-semibold">
                {sending ? <div className="spin-gold w-5 h-5" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer ma demande</>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
